import React, { useState, useEffect } from 'react';
import { XCircle, Search } from 'lucide-react';
import { Municipality, getMunicipalities, getAllMunicipalities } from '@/lib/data';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { toast } from "sonner";

interface ComparisonSelectorProps {
  selectedMunicipalities: Municipality[];
  onMunicipalitySelect: (municipality: Municipality) => void;
  onMunicipalityRemove: (municipalityId: string) => void;
}

const ComparisonSelector: React.FC<ComparisonSelectorProps> = ({
  selectedMunicipalities,
  onMunicipalitySelect,
  onMunicipalityRemove
}) => {
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [filteredMunicipalities, setFilteredMunicipalities] = useState<Municipality[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [totalEstimated, setTotalEstimated] = useState(0);
  const [totalMunicipalities, setTotalMunicipalities] = useState(0);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        // Bare hent data hvis vi ikke har noen kommuner eller trenger å oppdatere basert på valgte kommuner
        if (!municipalities.length || selectedMunicipalities.length > 0) {
          const data = await getAllMunicipalities();
          const estimatedCount = data.filter(m => m.historicalData.some(d => d.isEstimated)).length;
          setTotalEstimated(estimatedCount);
          setTotalMunicipalities(data.length);
          setMunicipalities(data);
          setFilteredMunicipalities(data.filter(
            m => !selectedMunicipalities.some(sm => sm.id === m.id)
          ));
        }
      } catch (error) {
        console.error('Error fetching all municipalities:', error);
        try {
          const fallbackData = await getMunicipalities();
          setMunicipalities(fallbackData);
          setFilteredMunicipalities(fallbackData.filter(
            m => !selectedMunicipalities.some(sm => sm.id === m.id)
          ));
          toast.warning("Kunne ikke hente alle kommuner. Søk og sammenligning vil være begrenset til utvalgte kommuner.");
        } catch (fallbackError) {
          console.error('Error fetching municipalities fallback:', fallbackError);
          toast.error("Kunne ikke hente kommunedata. Prøv igjen senere.");
        } finally {
          setIsLoading(false);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [selectedMunicipalities]);
  
  useEffect(() => {
    // Enhanced search logic for better matching
    if (!searchTerm.trim()) {
      // If search is empty, show all unselected municipalities
      setFilteredMunicipalities(municipalities.filter(
        m => !selectedMunicipalities.some(sm => sm.id === m.id)
      ));
      return;
    }
    
    const searchTermLower = searchTerm.toLowerCase().trim();
    
    // Filter municipalities with super flexible matching
    const filtered = municipalities.filter(m => {
      // Skip already selected municipalities
      if (selectedMunicipalities.some(sm => sm.id === m.id)) {
        return false;
      }
      
      const nameLower = m.name.toLowerCase();
      
      // Direct match in name
      if (nameLower.includes(searchTermLower)) {
        return true;
      }
      
      // Match with "kommune" appended if not already there
      if (!nameLower.includes("kommune") && 
          (`${nameLower} kommune`).includes(searchTermLower)) {
        return true;
      }
      
      // Match without "kommune" if it's in the name
      if (nameLower.includes("kommune") && 
          nameLower.replace(" kommune", "").includes(searchTermLower)) {
        return true;
      }
      
      // Split searches like "søndre land" into words
      const searchWords = searchTermLower.split(/\s+/);
      if (searchWords.length > 1) {
        // Check if ALL words in the search are in the municipality name (in any order)
        const allWordsMatch = searchWords.every(word => 
          nameLower.includes(word) || 
          word === "kommune" || 
          word.length < 3 // Skip very short words
        );
        if (allWordsMatch) return true;
      }
      
      return false;
    });
    
    setFilteredMunicipalities(filtered);
  }, [searchTerm, municipalities, selectedMunicipalities]);
  
  const handleSelect = (municipalityId: string) => {
    const municipality = municipalities.find(m => m.id === municipalityId);
    if (municipality) {
      onMunicipalitySelect(municipality);
      setSearchTerm(''); // Reset search after selection
    }
  };
  
  const maxSelections = 5;
  const canSelectMore = selectedMunicipalities.length < maxSelections;
  
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-100">
      <h3 className="text-lg font-semibold mb-4">Sammenlign kommuner</h3>
      
      <div className="flex flex-col gap-4">
        {isLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : (
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Søk blant alle kommuner..."
              className="pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={!canSelectMore}
            />
            
            {canSelectMore && searchTerm.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                {filteredMunicipalities.length > 0 ? (
                  <>
                    {filteredMunicipalities.slice(0, 10).map((municipality) => (
                      <div 
                        key={municipality.id}
                        className="px-3 py-2 cursor-pointer hover:bg-muted"
                        onClick={() => handleSelect(municipality.id)}
                      >
                        {municipality.name}
                      </div>
                    ))}
                    {filteredMunicipalities.length > 10 && (
                      <div className="px-3 py-2 text-xs text-muted-foreground">
                        +{filteredMunicipalities.length - 10} flere kommuner
                      </div>
                    )}
                  </>
                ) : (
                  <div className="px-3 py-2 text-sm text-muted-foreground">
                    Ingen treff. Prøv et annet søkeord.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        <div className="flex flex-wrap gap-2 mt-2">
          {selectedMunicipalities.map((municipality) => (
            <Badge 
              key={municipality.id}
              variant="secondary"
              className="flex items-center gap-1 py-1.5"
            >
              {municipality.name}
              <XCircle 
                className="h-3.5 w-3.5 ml-1 cursor-pointer" 
                onClick={() => onMunicipalityRemove(municipality.id)}
              />
            </Badge>
          ))}
        </div>
      </div>
      
      <div className="mt-4 text-xs text-muted-foreground">
        Velg opptil {maxSelections} kommuner å sammenligne samtidig 
        {!canSelectMore && (
          <span className="text-destructive ml-1">
            (Maks antall nådd)
          </span>
        )}
      </div>
    </div>
  );
};

export default ComparisonSelector;
